import React from 'react'
import styled from 'styled-components/macro'
import {
  HARBOUR_ASK_SALES_WARMED_CAPTION,
  HARBOUR_ASK_SALES_WARMED_LABEL,
  HARBOUR_COLUMN,
  HARBOUR_THRIVING_CAPTION,
  HARBOUR_THRIVING_LABEL,
  formatHarbourThrivingChip,
} from 'src/constants/harbour'
import {
  capHarbourNeedsYouCards,
  getHarbourColumnEmptyCopy,
  getHarbourNeedsYouMeta,
} from 'src/utils/harbourBoard'
import { HARBOUR_TOOLTIPS } from 'src/utils/harbourTooltips'
import HarbourCard from './HarbourCard'
import HarbourColumn from './HarbourColumn'

function SkeletonCard() {
  return (
    <Ghost>
      <Bar $width='70%' />
      <Bar $width='45%' />
      <Bar $width='90%' />
      <Bar $width='55%' />
    </Ghost>
  )
}

/**
 * Needs you — ranked at-risk queue, cap 30, iceberg caption. Empty stays
 * empty. Optional Thriving is a separate sales pile, never a silent refill.
 */
const HarbourNeedsYou = ({
  cards = [],
  needsYou = {},
  thriving = {},
  askSales = {},
  showThriving = false,
  loading = false,
  error = null,
  now,
  startBusyVenueId = null,
  onRetry,
  onSelectVenue,
  onStartWork,
  onAskSales,
  onAskProduct,
  onToggleThriving,
}) => {
  const visible = capHarbourNeedsYouCards(cards)
  const meta = getHarbourNeedsYouMeta(needsYou, visible)
  const thrivingTotal = Number(thriving.total) || 0
  const thrivingCards = capHarbourNeedsYouCards(thriving.cards || [])
  const thrivingAvailable = thriving.available !== false && thrivingTotal > 0
  const showChip = thrivingAvailable
  const showPile =
    Boolean(showThriving) && showChip && !loading && thrivingCards.length > 0
  const askSalesCards = capHarbourNeedsYouCards(askSales.cards || [])
  const showAskSales = askSalesCards.length > 0

  return (
    <Wrap data-testid='harbour-needs-you' title={HARBOUR_TOOLTIPS.needsYou}>
      <HarbourColumn
        column={HARBOUR_COLUMN.NEEDS_YOU}
        cards={visible}
        caption={meta.caption}
        detail={meta.detail}
        emptyCopy={getHarbourColumnEmptyCopy(HARBOUR_COLUMN.NEEDS_YOU)}
        loading={loading && !showPile && !showAskSales}
        error={error}
        skeleton={
          <Skeleton data-testid='harbour-needs-you-skeleton'>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </Skeleton>
        }
        now={now}
        startBusyVenueId={startBusyVenueId}
        onRetry={onRetry}
        onSelectVenue={onSelectVenue}
        onStartWork={onStartWork}
        onAskSales={onAskSales}
        onAskProduct={onAskProduct}
      />

      {showAskSales ? (
        <AskSalesWarmed
          data-testid='harbour-ask-sales-warmed'
          title={HARBOUR_TOOLTIPS.askSalesWarmed}
        >
          <AskSalesHeader>
            <AskSalesHeading>
              <AskSalesLabel>{HARBOUR_ASK_SALES_WARMED_LABEL}</AskSalesLabel>
              <AskSalesCaption>
                {HARBOUR_ASK_SALES_WARMED_CAPTION}
              </AskSalesCaption>
            </AskSalesHeading>
            <AskSalesCount data-testid='harbour-ask-sales-warmed-count'>
              {askSalesCards.length}
            </AskSalesCount>
          </AskSalesHeader>
          <CardList>
            {askSalesCards.map(card => (
              <HarbourCard
                key={card.venueId}
                card={card}
                now={now}
                startBusy={startBusyVenueId === card.venueId}
                onSelectVenue={onSelectVenue}
                onStartWork={onStartWork}
                onAskSales={onAskSales}
                onAskProduct={onAskProduct}
              />
            ))}
          </CardList>
        </AskSalesWarmed>
      ) : null}

      {showChip ? (
        <ChipRow>
          <Chip
            type='button'
            data-testid='harbour-thriving-chip'
            aria-pressed={Boolean(showThriving)}
            $pressed={Boolean(showThriving)}
            title={HARBOUR_TOOLTIPS.thriving}
            onClick={() => onToggleThriving?.()}
          >
            {formatHarbourThrivingChip(thrivingTotal)}
          </Chip>
          <Hint>{HARBOUR_THRIVING_CAPTION}</Hint>
        </ChipRow>
      ) : null}

      {showPile ? (
        <Thriving
          data-testid='harbour-thriving'
          title={HARBOUR_TOOLTIPS.thriving}
        >
          <ThrivingHeader>
            <ThrivingHeading>
              <ThrivingLabel>{HARBOUR_THRIVING_LABEL}</ThrivingLabel>
              <ThrivingCaption>{HARBOUR_THRIVING_CAPTION}</ThrivingCaption>
            </ThrivingHeading>
            <ThrivingCount data-testid='harbour-thriving-count'>
              {thrivingCards.length}
            </ThrivingCount>
          </ThrivingHeader>
          {thrivingCards.length === 0 ? null : (
            <CardList>
              {thrivingCards.map(card => (
                <HarbourCard
                  key={card.venueId}
                  card={card}
                  now={now}
                  startBusy={startBusyVenueId === card.venueId}
                  onSelectVenue={onSelectVenue}
                  onStartWork={onStartWork}
                  onAskSales={onAskSales}
                  onAskProduct={onAskProduct}
                />
              ))}
            </CardList>
          )}
        </Thriving>
      ) : null}
    </Wrap>
  )
}

const Wrap = styled.div`
  min-width: 16.5rem;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Skeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Ghost = styled.div`
  padding: 12px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e6e8eb;
`

const Bar = styled.div`
  height: 10px;
  width: ${({ $width }) => $width || '100%'};
  margin-bottom: 8px;
  border-radius: 4px;
  background: #e6e8eb;
`

const ChipRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 0 2px;
`

const Chip = styled.button`
  margin: 0;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colours.oliveGrey || '#c5cdc8'};
  background: ${({ $pressed }) => ($pressed ? '#5B8A72' : '#fff')};
  color: ${({ $pressed }) => ($pressed ? '#fff' : 'inherit')};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`

const Hint = styled.p`
  margin: 0;
  font-size: 11px;
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
`

const Thriving = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 16.5rem;
  padding: 10px;
  border-top: 3px solid
    ${({ theme }) => theme.colours.harbourGreen || '#8AA89A'};
  background: #f7faf8;
  border-radius: 8px;
`

const ThrivingHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`

const ThrivingHeading = styled.div`
  min-width: 0;
`

const ThrivingLabel = styled.h4`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
`

const ThrivingCaption = styled.p`
  margin: 4px 0 0;
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const ThrivingCount = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  background: #fff;
  font-size: 12px;
  font-weight: 700;
`

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 70vh;
  overflow: auto;
`

const AskSalesWarmed = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 16.5rem;
  padding: 10px;
  border-top: 3px solid #c9a227;
  background: #fffbf0;
  border-radius: 8px;
`

const AskSalesHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`

const AskSalesHeading = styled.div`
  min-width: 0;
`

const AskSalesLabel = styled.h4`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
`

const AskSalesCaption = styled.p`
  margin: 4px 0 0;
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const AskSalesCount = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  background: #fff;
  font-size: 12px;
  font-weight: 700;
`

export default HarbourNeedsYou
